/**
 * Harness 自动化层 FR-C — Evals 评估体系
 *
 * 职责：跑 N 局对打 + 代码质量分析，汇总 EvalsReport。
 * 持久化到 localStorage `battlecode-evals-history`。
 *
 * 设计决策：
 *   - 代码质量分析本地实现（不耗 API），用正则检测
 *   - 对打用 EngineScheduler 跑 N 局
 *   - 持久化供历史对比
 */

import { EngineScheduler } from "../engine/engine-scheduler";
import { getMapById } from "../engine/map-utils";
import type { BattleState } from "../engine/types";
import { TANK_STATS } from "../engine/tank-stats";

const EVALS_STORAGE_KEY = "battlecode-evals-history";

// ===== 类型定义 =====

export interface EvalsConfig {
  battleCount: number;
  aiOpponents: string[];
  mapId: string;
}

export interface CodeQualityResult {
  hasCommentHeader: boolean;
  blockCommentDensity: number;
  apiCallComments: number;
  forbiddenPatterns: string[];
}

export interface EvalsMetrics {
  winRate: number;
  avgSurvivalTicks: number;
  avgDamageDealt: number;
  avgShotsFired: number;
  avgShotsHit: number;
  codeQuality: CodeQualityResult;
  avgThinkTimeMs: number;
  maxThinkTimeMs: number;
  softTimeoutCount: number;
  hardTimeoutCount: number;
  strategySignature: string;
}

export interface PerBattleResult {
  winner: "team_a" | "team_b" | "draw";
  totalTicks: number;
  playerDamage: number;
  aiDamage: number;
  playerShotsFired: number;
  playerShotsHit: number;
}

export interface EvalsReport {
  timestamp: number;
  botCodeHash: string;
  config: EvalsConfig;
  metrics: EvalsMetrics;
  perBattleResults: PerBattleResult[];
}

// ===== 代码质量分析（本地，不耗 API）=====

/**
 * 分析 bot 代码的代码质量指标
 */
export function analyzeCodeQuality(code: string): CodeQualityResult {
  const lines = code.split("\n");
  const totalLines = lines.length;

  // 检测 5a 能力说明
  const hasCommentHeader =
    code.includes("# === Bot 能力说明 ===") &&
    code.includes("# === /Bot 能力说明 ===");

  // 块注释密度：注释行 / 总行
  let commentLines = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("#") && !trimmed.startsWith("# ===")) {
      commentLines++;
    }
  }
  const blockCommentDensity = totalLines > 0 ? commentLines / totalLines : 0;

  // 关键调用注释数
  let apiCallComments = 0;
  for (let i = 1; i < lines.length; i++) {
    const prev = lines[i - 1].trim();
    const curr = lines[i].trim();
    if (
      prev.startsWith("#") &&
      /\b(self\.shoot|self\.move|self\.scan|self\.nearest_cover)\s*\(/.test(curr)
    ) {
      apiCallComments++;
    }
  }

  // 禁止模式检测
  const forbiddenPatterns: string[] = [];
  if (/^import\s+/m.test(code)) forbiddenPatterns.push("import 语句(禁止)");
  if (/try\s*:/m.test(code)) forbiddenPatterns.push("try/except(禁止)");
  if (/\benemy\s*\(\s*\)/.test(code)) forbiddenPatterns.push("enemy() 误用(应是 self.enemy)");
  if (/\bmove\s*\(/.test(code) && !/self\.move/.test(code))
    forbiddenPatterns.push("move() 误用(应是 self.move())");
  if (/\bshoot\s*\(\s*\)/.test(code) && !/self\.shoot/.test(code))
    forbiddenPatterns.push("shoot() 误用(应是 self.shoot())");

  return {
    hasCommentHeader,
    blockCommentDensity,
    apiCallComments,
    forbiddenPatterns,
  };
}

// ===== 对打执行 =====

interface BattleSimResult {
  winner: "team_a" | "team_b" | "draw";
  totalTicks: number;
  playerDamage: number;
  aiDamage: number;
  playerShotsFired: number;
  playerShotsHit: number;
}

/**
 * 跑一局模拟对打
 * 用 EngineScheduler 加上监听器提取结果。
 * 最大 1800 ticks(60s @ 30Hz) 或双方任一阵亡。
 */
async function runSingleSimulation(
  playerCode: string,
  aiCode: string,
  mapId: string,
): Promise<BattleSimResult> {
  return new Promise((resolve) => {
    const engine = new EngineScheduler();
    let playerDamage = 0;
    let aiDamage = 0;
    let playerShotsFired = 0;
    let playerShotsHit = 0;

    const map = getMapById(mapId);
    const dims = map.dimensions ?? { width: 25, height: 25 };
    const spawnA = map.spawns?.team_a?.[0] ?? { x: 0, y: 12 };
    const spawnB = map.spawns?.team_b?.[0] ?? { x: 24, y: 12 };

    const initialState: BattleState = {
      tick: 0,
      map_id: mapId,
      map_size: { width: dims.width, height: dims.height },
      self: {
        team: "team_a",
        tank_index: 0,
        position: { x: spawnA.x, y: spawnA.y },
        spawn: { x: spawnA.x, y: spawnA.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        cooldown_left: 0,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      },
      enemy: {
        tank_index: 0,
        position: { x: spawnB.x, y: spawnB.y },
        spawn: { x: spawnB.x, y: spawnB.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        is_alive: true,
        is_visible: false,
        last_known_position: null,
      },
      nearest_cover: null,
    };

    const removeListener = engine.on((event) => {
      if (event.type === "hit") {
        if (event.shooter === "team_a") {
          playerShotsFired++;
          playerShotsHit++;
          playerDamage += event.damage;
        } else {
          aiDamage += event.damage;
        }
      } else if (event.type === "battle_end") {
        resolve({
          winner: event.winner,
          totalTicks: 0, // 由 tick 事件更新
          playerDamage,
          aiDamage,
          playerShotsFired,
          playerShotsHit,
        });
      }
    });

    // 记录 tick 数
    let lastTick = 0;
    const tickListener = engine.on((event) => {
      if (event.type === "tick") {
        lastTick = event.tickNum;
      }
    });

    engine.start(playerCode, initialState, aiCode).then(() => {
      removeListener();
      tickListener();
      // 如果 engine 正常结束但没有 battle_end 事件，手动 resolve
      resolve({
        winner: "draw",
        totalTicks: lastTick,
        playerDamage,
        aiDamage,
        playerShotsFired,
        playerShotsHit,
      });
    });

    // 兜底：超时 90s 强制结束
    setTimeout(() => {
      engine.stop();
      resolve({
        winner: "draw",
        totalTicks: lastTick,
        playerDamage,
        aiDamage,
        playerShotsFired,
        playerShotsHit,
      });
    }, 90000);
  });
}

// ===== 评估入口 =====

/**
 * 跑 N 局对打 + 代码质量分析，返回 EvalsReport
 */
export async function runEvals(
  botCode: string,
  config: EvalsConfig,
  onProgress?: (current: number, total: number) => void,
): Promise<EvalsReport> {
  const codeQuality = analyzeCodeQuality(botCode);
  const aiCode = config.aiOpponents[0] ?? "ai_easy_aggressive";

  const perBattleResults: PerBattleResult[] = [];

  // 跑 N 局
  for (let i = 0; i < config.battleCount; i++) {
    onProgress?.(i + 1, config.battleCount);
    const sim = await runSingleSimulation(botCode, aiCode, config.mapId);
    perBattleResults.push({
      winner: sim.winner,
      totalTicks: sim.totalTicks,
      playerDamage: sim.playerDamage,
      aiDamage: sim.aiDamage,
      playerShotsFired: sim.playerShotsFired,
      playerShotsHit: sim.playerShotsHit,
    });
  }

  // 汇总指标
  const wins = perBattleResults.filter((r) => r.winner === "team_a").length;
  const winRate = config.battleCount > 0 ? wins / config.battleCount : 0;
  const avgSurvivalTicks =
    config.battleCount > 0
      ? perBattleResults.reduce((s, r) => s + r.totalTicks, 0) / config.battleCount
      : 0;
  const avgDamage =
    config.battleCount > 0
      ? perBattleResults.reduce((s, r) => s + r.playerDamage, 0) / config.battleCount
      : 0;
  const totalShotsFired = perBattleResults.reduce((s, r) => s + r.playerShotsFired, 0);
  const totalShotsHit = perBattleResults.reduce((s, r) => s + r.playerShotsHit, 0);
  const avgShotsFired = config.battleCount > 0 ? totalShotsFired / config.battleCount : 0;
  const avgShotsHit = config.battleCount > 0 ? totalShotsHit / config.battleCount : 0;

  // 策略签名：基于代码关键词
  const strategySignature = computeStrategySignature(botCode);

  return {
    timestamp: Date.now(),
    botCodeHash: simpleHash(botCode),
    config,
    metrics: {
      winRate,
      avgSurvivalTicks,
      avgDamageDealt: avgDamage,
      avgShotsFired,
      avgShotsHit,
      codeQuality,
      avgThinkTimeMs: 0, // 需引擎层暴露 per-tick 耗时，留 v2+ 引擎埋点
      maxThinkTimeMs: 0,
      softTimeoutCount: 0, // 需引擎层暴露超时计数，留 v2+ 引擎埋点
      hardTimeoutCount: 0,
      strategySignature,
    },
    perBattleResults,
  };
}

/** 策略签名：分析代码关键词生成 */
function computeStrategySignature(code: string): string {
  const keywords: string[] = [];
  if (/self\.shoot/.test(code)) keywords.push("shoot");
  if (/self\.move/.test(code)) keywords.push("move");
  if (/self\.scan/.test(code)) keywords.push("scan");
  if (/self\.nearest_cover/.test(code)) keywords.push("cover");
  if (/self\.distance_to/.test(code)) keywords.push("distance");
  if (/self\.spawn_point/.test(code)) keywords.push("spawn");

  const ratios: string[] = [];
  const shootCount = (code.match(/self\.shoot/g) ?? []).length;
  const moveCount = (code.match(/self\.move/g) ?? []).length;
  const scanCount = (code.match(/self\.scan/g) ?? []).length;
  const total = shootCount + moveCount + scanCount || 1;
  ratios.push(
    `S${Math.round((shootCount / total) * 100)}M${Math.round((moveCount / total) * 100)}Sc${Math.round((scanCount / total) * 100)}`,
  );

  return keywords.join("/") + " · " + ratios.join("");
}

/**
 * 对比两次 Evals 报告
 */
export function compareEvals(
  before: EvalsReport,
  after: EvalsReport,
): {
  winRateDelta: number;
  codeQualityDelta: Array<{ field: string; before: number; after: number }>;
  summary: string;
} {
  const winRateDelta = after.metrics.winRate - before.metrics.winRate;

  const codeQualityDelta = [
    {
      field: "块注释密度",
      before: before.metrics.codeQuality.blockCommentDensity,
      after: after.metrics.codeQuality.blockCommentDensity,
    },
    {
      field: "关键调用注释数",
      before: before.metrics.codeQuality.apiCallComments,
      after: after.metrics.codeQuality.apiCallComments,
    },
  ];

  const trend = winRateDelta >= 0 ? "提升" : "下降";
  const summary = `胜率 ${trend} ${Math.abs(winRateDelta * 100).toFixed(0)}%（${(before.metrics.winRate * 100).toFixed(0)}% → ${(after.metrics.winRate * 100).toFixed(0)}%）`;

  return { winRateDelta, codeQualityDelta, summary };
}

// ===== 持久化 =====

import { dataProxy } from "../core/data-proxy.js";

export function loadEvalsHistory(): EvalsReport[] {
  return dataProxy.get<EvalsReport[]>("evals-history") ?? [];
}

export function saveEvalsReport(report: EvalsReport): void {
  const history = loadEvalsHistory();
  history.push(report);
  // 最多保留 20 条
  if (history.length > 20) {
    history.splice(0, history.length - 20);
  }
  dataProxy.set("evals-history", history);
}

export { EVALS_STORAGE_KEY };

/** 简单 hash（非加密，仅用于比对代码是否变化） */
function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}
